import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface HighlightedItem {
  entityId: string;
  entityType: string;
  timestamp: number;
  notificationId?: string;
}

interface HighlightContextType {
  highlightedItems: HighlightedItem[];
  highlightItem: (entityId: string, entityType: string, notificationId?: string) => void;
  clearHighlight: (entityId: string, entityType: string) => void;
  clearAllHighlights: () => void;
  isHighlighted: (entityId: string, entityType: string) => boolean;
  getHighlightedItem: (entityId: string, entityType: string) => HighlightedItem | null;
}

const HighlightContext = createContext<HighlightContextType | undefined>(undefined);

export const useHighlightContext = () => {
  const context = useContext(HighlightContext);
  if (!context) {
    throw new Error('useHighlightContext must be used within a HighlightProvider');
  }
  return context;
};

interface HighlightProviderProps {
  children: React.ReactNode;
}

export const HighlightProvider: React.FC<HighlightProviderProps> = ({ children }) => {
  const [highlightedItems, setHighlightedItems] = useState<HighlightedItem[]>([]);

  // Load highlighted items from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('highlightedItems');
    if (stored) {
      try {
        const items: HighlightedItem[] = JSON.parse(stored);
        // Filter out items older than 5 minutes
        const now = Date.now();
        const validItems = items.filter(item => now - item.timestamp < 5 * 60 * 1000);
        setHighlightedItems(validItems);
        sessionStorage.setItem('highlightedItems', JSON.stringify(validItems));
      } catch (_error) {
        // console.error('Error loading highlighted items:', error);
      }
    }
  }, []);

  // Save highlighted items to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('highlightedItems', JSON.stringify(highlightedItems));
  }, [highlightedItems]);

  const highlightItem = useCallback((entityId: string, entityType: string, notificationId?: string) => {
    setHighlightedItems(prev => {
      // Remove any existing highlight for this item
      const filtered = prev.filter(item => !(item.entityId === entityId && item.entityType === entityType));
      
      // Add new highlight
      const newItem: HighlightedItem = {
        entityId,
        entityType,
        timestamp: Date.now(),
        ...(notificationId && { notificationId })
      };
      
      return [...filtered, newItem];
    });
  }, []);

  const clearHighlight = useCallback((entityId: string, entityType: string) => {
    setHighlightedItems(prev => 
      prev.filter(item => !(item.entityId === entityId && item.entityType === entityType))
    );
  }, []);

  const clearAllHighlights = useCallback(() => {
    setHighlightedItems([]);
  }, []);

  const isHighlighted = useCallback((entityId: string, entityType: string) => {
    return highlightedItems.some(item => 
      item.entityId === entityId && item.entityType === entityType
    );
  }, [highlightedItems]);

  const getHighlightedItem = useCallback((entityId: string, entityType: string) => {
    return highlightedItems.find(item => 
      item.entityId === entityId && item.entityType === entityType
    ) || null;
  }, [highlightedItems]);

  const value: HighlightContextType = {
    highlightedItems,
    highlightItem,
    clearHighlight,
    clearAllHighlights,
    isHighlighted,
    getHighlightedItem
  };

  return (
    <HighlightContext.Provider value={value}>
      {children}
    </HighlightContext.Provider>
  );
};
